import React, { useEffect, useRef, useState } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useRole,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react';

function createPortalRoot(root) {
  const tooltipRoot = document.createElement('div');
  tooltipRoot.setAttribute('id', root);
  return tooltipRoot;
}

export function useFloatingTooltip(tooltipMessage, restProps) {
  const { hasReference, position, root = 'tooltip' } = restProps || {};

  const bodyRef = useRef(window.appRootMountPoint || document.querySelector('body'));
  const portalRootRef = root ? useRef(document.getElementById(root) || createPortalRoot(root)) : null;

  useEffect(() => {
    if (root) {
      if (document.getElementById(root) === null) {
        bodyRef.current.appendChild(portalRootRef.current);
      }
      else {
        portalRootRef.current = document.getElementById(root);
      }
    }
  }, []);

  function generateGetBoundingClientRect(x = 0, y = 0) {
    return () => ({
      width: 0,
      height: 0,
      top: y,
      right: x,
      bottom: y,
      left: x,
    });
  }

  const [virtualElement, setVirtualElement] = useState({
    getBoundingClientRect: generateGetBoundingClientRect(),
  });

  const [isTooltipOpen, setTooltipIsOpen] = useState(false);

  const elements = hasReference ? undefined : {
    reference: virtualElement,
  };

  const { refs, floatingStyles, update, context } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setTooltipIsOpen,
    placement: position || 'top',
    // Make sure the tooltip stays on the screen
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
    ],
    elements,
  });

  // Event listeners to change the open state
  const hover = useHover(context, { move: true });
  const focus = useFocus(context);
  // Role props for screen readers
  const role = useRole(context, { role: 'tooltip' });

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    role,
  ]);

  const showTooltip = () => {
    if (tooltipMessage) {
      setTooltipIsOpen(true);
    }
  };

  const updateTooltip = (event) => {
    if (!isTooltipOpen) {
      showTooltip();
    }
    if (tooltipMessage) {
      virtualElement.getBoundingClientRect = generateGetBoundingClientRect(event.clientX, event.clientY);
      setVirtualElement(virtualElement);
      update();
    }
  };

  const hideTooltip = () => setTooltipIsOpen(false);

  const tooltipElement = (
    <>
      {isTooltipOpen && tooltipMessage && (
        <FloatingPortal root={portalRootRef}>
          <div
            className="popper-tooltip"
            role="tooltip"
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {tooltipMessage}
          </div>
        </FloatingPortal>
      )}
    </>
  );

  return {
    tooltipElement,
    showTooltip,
    hideTooltip,
    updateTooltip,
    isTooltipOpen,
    ref: hasReference ? refs.setReference : null,
    refProps: hasReference ? getReferenceProps : null,
  };

}
